import { get, isUndefined, lowerCase, map } from 'lodash-es'
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Space,
} from 'antd'
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { CerfaPlanningType } from '@autoriz/shared/cerfa'
import { Project } from 'types'

import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'

import { PlanningFileTypeName } from 'src/utils/config'
import { PlanningFileType } from 'src/utils/enums'

const ColoredTitle: React.FC<{
  title: string
}> = ({ title }) => <h2 style={{ fontFamily: 'CairoPlay' }}>{title}</h2>

const PlanningTypeChecklistContainer = styled.div`
  p {
    margin-bottom: 0.5em;
  }
`
export type PlanningType = CerfaPlanningType | undefined

type PlanningTypeCondition = { text: string; color: string }

const PlanningTypeCard: React.FC<{
  id: PlanningType
  title: string
  planningType: PlanningType
  setPlanningType: (planning: PlanningType) => void
  conditions: PlanningTypeCondition[]
}> = ({ id, title, planningType, setPlanningType, conditions }) => {
  return (
    <Card
      key={id}
      hoverable
      data-cy={`planning-type-${lowerCase(id)}`}
      style={{
        backgroundColor: planningType == id ? 'lightgreen' : undefined,
      }}
      onClick={() => setPlanningType(id)}
    >
      <Card.Meta
        title={<ColoredTitle title={title} />}
        description={
          <PlanningTypeChecklistContainer>
            {map(conditions, ({ text, color }, idx) => (
              <p key={idx}>
                <CheckCircleOutlined
                  style={{ fontSize: '18px', color: color }}
                />
                &nbsp;
                {text}
              </p>
            ))}
          </PlanningTypeChecklistContainer>
        }
      />
    </Card>
  )
}

const PlanningTypeChoice: React.FC<{
  planningType: 'DP' | 'PC' | 'PCM' | undefined
  setPlanningType: (planning: PlanningType) => void
}> = ({ planningType, setPlanningType }) => {
  const dpConditions = useMemo(
    () =>
      map(
        [
          { text: 'confition 1' },
          { text: 'confition 2' },
          { text: 'confition 3' },
          { text: 'confition 4' },
        ],
        ({ text }) => ({ text: text, color: 'grey' })
      ),
    []
  )
  const pcConditions = useMemo(
    () =>
      map(
        [
          { text: 'confition 1' },
          { text: 'confition 2' },
          { text: 'confition 3' },
          { text: 'confition 4' },
        ],
        ({ text }) => ({ text: text, color: 'grey' })
      ),
    []
  )
  const pcmConditions = useMemo(
    () =>
      map(
        [
          { text: 'confition 1' },
          { text: 'confition 2' },
          { text: 'confition 3' },
          { text: 'confition 4' },
        ],
        ({ text }) => ({ text: text, color: 'grey' })
      ),
    []
  )

  return (
    <Row gutter={[18, 18]} justify="center" wrap style={{ width: '100%' }}>
      <Col>
        <PlanningTypeCard
          id="DP"
          planningType={planningType}
          setPlanningType={setPlanningType}
          title={PlanningFileTypeName[PlanningFileType.DP]}
          conditions={dpConditions}
        />
      </Col>
      <Col>
        <PlanningTypeCard
          id="PC"
          planningType={planningType}
          setPlanningType={setPlanningType}
          title={PlanningFileTypeName[PlanningFileType.PC]}
          conditions={pcConditions}
        />
      </Col>
      <Col>
        <PlanningTypeCard
          id="PCM"
          planningType={planningType}
          setPlanningType={setPlanningType}
          title={PlanningFileTypeName[PlanningFileType.PCM]}
          conditions={pcmConditions}
        />
      </Col>
    </Row>
  )
}

const SigningChoice: React.FC<{
  isSigning: boolean
  setSigningChoice: Dispatch<SetStateAction<boolean>>
}> = ({ isSigning, setSigningChoice }) => {
  return (
    <Radio.Group
      onChange={(event) => setSigningChoice(event.target.value)}
      value={isSigning}
      style={{ width: 'fit-content' }}
    >
      <Radio.Button value={true} data-cy="planning-signing-yes">
        Avec signature
      </Radio.Button>
      <Radio.Button value={false} data-cy="planning-signing-no">
        Sans signature
      </Radio.Button>
    </Radio.Group>
  )
}

const ABFChoice: React.FC<{
  isABF: boolean
  setABFChoice: Dispatch<SetStateAction<boolean>>
}> = ({ isABF, setABFChoice }) => {
  return (
    <Radio.Group
      onChange={(event) => setABFChoice(event.target.value)}
      value={isABF}
      style={{ width: 'fit-content' }}
    >
      <Radio.Button value={true} data-cy="planning-abf-yes">
        Avec ABF
      </Radio.Button>
      <Radio.Button value={false} data-cy="planning-abf-no">
        Sans ABF
      </Radio.Button>
    </Radio.Group>
  )
}

export const PlanningTypeModal: React.FC<{
  project: Project
  onSelect: (
    planningType: PlanningType,
    signing: boolean,
    abf: boolean,
    reserves: string | undefined
  ) => void
  onModalClose?: () => void
}> = ({ project, onSelect, onModalClose }) => {
  const planningFileType = get(project, 'planning_file_type', '') ?? ''
  const [planningType, setPlanningType] = useState<PlanningType | undefined>(
    (planningFileType ? get(planningFileType.split('_'), '[0]', undefined) : undefined) as PlanningType
  )
  const [isSigning, setSigningChoice] = useState<boolean>(
    planningFileType.includes('SIGNED')
  )
  const [isABF, setABFChoice] = useState<boolean>(
    planningFileType.includes('ABF')
  )
  const [reserves, setReserves] = useState<string | undefined>(project.reserves)

  const changePlanningType = (planning: PlanningType) => {
    setPlanningType(planning)
    setSigningChoice(false)
    setABFChoice(false)
  }

  const closeModal = () => {
    setPlanningType(undefined)
    setSigningChoice(false)
    setABFChoice(false)
    setReserves(undefined)
    onModalClose?.()
  }

  return (
    <Modal
      open={true}
      centered
      className="planning-type-modal"
      width={'70%'}
      style={{ maxWidth: '900px' }}
      closable
      onCancel={closeModal}
      footer={
        <Space>
          <Button
            danger
            onClick={closeModal}
            data-cy="action--cancel-planning-type-choice"
          >
            Annuler
          </Button>
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: '#00b96b',
              },
            }}
          >
            <Button
              type="primary"
              disabled={isUndefined(planningType)}
              data-cy="action--validate-planning-type-choice"
              onClick={() => {
                if (!isUndefined(planningType)) {
                  onSelect(planningType, isSigning, isABF, reserves)
                  closeModal()
                }
              }}
            >
              Valider
            </Button>
          </ConfigProvider>
        </Space>
      }
    >
      <PlanningTypeChoice
        planningType={planningType}
        setPlanningType={changePlanningType}
      />
      <Form.Item label={<b>Options</b>} labelCol={{ span: 24 }}>
        <Space wrap>
          {!isUndefined(planningType) && planningType !== 'DP' && (
            <SigningChoice
              isSigning={isSigning}
              setSigningChoice={setSigningChoice}
            />
          )}
          {!isUndefined(planningType) && (
            <ABFChoice isABF={isABF} setABFChoice={setABFChoice} />
          )}
        </Space>
      </Form.Item>
      <Form.Item
        label="Émettre des réserves"
        labelCol={{ span: 24 }}
        style={{ width: '100%' }}
      >
        <Input.TextArea
          value={reserves ?? ''}
          onChange={(event) => setReserves(event.target.value)}
          spellCheck
          lang="fr"
        />
      </Form.Item>
    </Modal>
  )
}
